import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Skeleton } from 'antd';
import { DashboardFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/Layout';
import { useSecureService } from '../../store/service';
import { useAuth } from '../../hooks/useAuth';
import styles from './Dashboard.module.scss';

type ProjectMetrics = {
  total: number;
  needsReview: number;
  updated: number;
  approved: number;
};

const Dashboard: React.FC = () => {
  const { getEntryCount, getUserDashboard, getUserNotifications } = useSecureService();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all');
  const [dashboardData, setDashboardData] = useState({
    internal: { total: 0, needsReview: 0, updated: 0, approved: 0 } as ProjectMetrics,
    external: { total: 0, needsReview: 0, updated: 0, approved: 0 } as ProjectMetrics,
    notificationsCount: 0,
    organizationsCount: 0,
    usersCount: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, [timeFilter]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate(timeFilter);
      const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';

      const [countResult, userDashboardResult, notificationsResult] = await Promise.all([
        getEntryCount(undefined, startDate),
        isAdmin ? getUserDashboard(startDate) : Promise.resolve(undefined as any),
        getUserNotifications()
      ]);

      const count = countResult?.data;
      const notifications = notificationsResult?.data?.notifications || [];
      const unreadNotifications = Array.isArray(notifications)
        ? notifications.filter((n: any) => !n?.isRead).length
        : (user?.unreadNotificationCount || 0);

      setDashboardData({
        internal: {
          total: count?.totalInternalProjects ?? 0,
          needsReview: count?.totalInternalNeedsReviewEntries ?? 0,
          updated: count?.totalInternalUpdatedEntries ?? 0,
          approved: count?.totalInternalApprovedEntries ?? 0
        },
        external: {
          total: count?.totalExternalProjects ?? 0,
          needsReview: count?.totalExternalNeedsReviewEntries ?? 0,
          updated: count?.totalExternalUpdatedEntries ?? 0,
          approved: count?.totalExternalApprovedEntries ?? 0
        },
        notificationsCount: unreadNotifications,
        organizationsCount: isAdmin ? (userDashboardResult?.data?.organizationsCount || 0) : 0,
        usersCount: isAdmin ? (userDashboardResult?.data?.usersCount || 0) : 0
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (filter: string) => {
    const now = new Date();
    switch (filter) {
      case 'thisMonth':
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      case 'thisYear':
        return new Date(now.getFullYear(), 0, 1).toISOString();
      case 'all':
        return undefined;
      default:
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    }
  };

  const timeFilterItems = [
    { key: 'thisMonth', label: 'This Month' },
    { key: 'thisYear', label: 'This Year' },
    { key: 'all', label: 'All' }
  ];

  const handleSeeAllClick = () => {
    navigate('/results?type=notifications');
  };

  const handleSectionCardClick = (section: 'internal' | 'external', cardType: string) => {
    const basePath = section === 'internal' ? '/internal-projects' : '/external-projects';
    if (cardType === 'total') {
      navigate(basePath);
      return;
    }
    // Needs Review / Updated / Approved -> entry list with filters
    const startDate = getStartDate(timeFilter);
    const statusMap: Record<string, string> = {
      needsReview: 'NeedsReview',
      updated: 'Updated',
      approved: 'Approved'
    };
    const status = statusMap[cardType];
    if (status) {
      const params = new URLSearchParams({ type: 'entry', status });
      if (startDate) params.set('startDate', startDate);
      params.set('scope', section === 'internal' ? 'Internal' : 'External');
      navigate(`/results?${params.toString()}`);
      return;
    }
    navigate(basePath);
  };

  const buildProjectCards = (metrics: ProjectMetrics) => [
    {
      title: 'Total',
      value: metrics.total,
      description: 'All created projects.',
      color: 'var(--app-accent-color)',
      type: 'total',
      clickable: true
    },
    {
      title: 'Needs Review',
      value: metrics.needsReview,
      description: 'Entries recently modified or revised',
      color: '#FFC107',
      type: 'needsReview',
      clickable: true
    },
    {
      title: 'Updated',
      value: metrics.updated,
      description: 'Entries recently modified or revised',
      color: '#E91E63',
      type: 'updated',
      clickable: true
    },
    {
      title: 'Approved',
      value: metrics.approved,
      description: 'Entries that have been reviewed & approved',
      color: 'var(--app-accent-color)',
      type: 'approved',
      clickable: true
    }
  ];

  const directoryCards = [
    {
      title: 'Total Number of Brands',
      value: dashboardData.organizationsCount,
      description: 'Brands registered on the platform.',
      color: '#2196F3',
      type: 'brands',
      clickable: false
    },
    {
      title: 'Total Number of Users',
      value: dashboardData.usersCount,
      description: 'All users who have accessed the platform.',
      color: '#9C27B0',
      type: 'users',
      clickable: false
    }
  ];

  const internalCards = buildProjectCards(dashboardData.internal);
  const externalCards = buildProjectCards(dashboardData.external);
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const canSeeInternalProjects = !!user && !['BrandAccountViewer', 'Guest'].includes(user.role);
  return (
    <MainLayout title="Dashboard" icon={<DashboardFilled />}>
      <div className={styles.dashboardContainer}>
        {/* Top Banner */}
        <div className={styles.topBannerWrapper}>
          <Card styles={{ body: { padding: 0 } }} className={styles.topBanner}>
            <div className={styles.linesSvg}>
              <img src="/images/lines.svg" alt="Lines" />
            </div>
            <Row gutter={0}>
              <Col span={14}>
                <div className={styles.bannerLeft}>
                  <div className={styles.statusTag}>Notifications</div>
                  <div className={styles.statusNumber}>
                  {loading ? (
                    <Skeleton.Input active size="small" style={{ width: 48, minWidth: 48, verticalAlign: 'middle' }} />
                  ) : (
                    dashboardData.notificationsCount
                  )}
                </div>
                  <div className={styles.statusText}>unread notifications in your inbox.</div>
                </div>
              </Col>
              <Col span={10} className={styles.bannerRightCol}>
                <div className={styles.bannerRight}>
                  <Button type="primary" className={styles.seeAllButton} onClick={handleSeeAllClick}>
                    See All
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Overview Section */}
        <div className={styles.overviewSection}>
          <div className={styles.overviewHeader}>
            <h2>Overview</h2>
            <div className={styles.timeFilters}>
              {timeFilterItems.map(item => (
                <span
                  key={item.key}
                  className={`${styles.timeFilter} ${timeFilter === item.key ? styles.active : ''}`}
                  onClick={() => setTimeFilter(item.key)}
                >
                  {item.label}
                </span>
              ))}
            </div>
          </div>

          {canSeeInternalProjects && (
            <div className={styles.sectionBlock}>
              <h3 className={styles.sectionTitle}>Internal Projects</h3>
              <Row gutter={[16, 16]}>
                {internalCards.map((card, index) => (
                  <Col xs={24} sm={12} lg={6} key={`internal-${index}`}>
                    <Card
                      styles={{ body: { padding: 0 } }}
                      className={`${styles.dataCard} ${card.clickable ? styles.clickable : ''}`}
                      onClick={card.clickable ? () => handleSectionCardClick('internal', card.type) : undefined}
                      data-color="glow"
                    >
                      <div className={styles.cardContent}>
                        <div className={styles.statusTag} style={{ background: card.color, borderColor: '#fff', color: '#fff' }}>
                          {card.title}
                        </div>
                        <div className={styles.statusNumber} style={{ color: card.color }}>
                          {loading ? (
                            <Skeleton.Input active size="small" className={styles.cardSkeleton} />
                          ) : (
                            card.value
                          )}
                        </div>
                        <div className={styles.statusText}>
                          {card.description}
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          <div className={styles.sectionBlock}>
            <h3 className={styles.sectionTitle}>External Projects</h3>
            <Row gutter={[16, 16]}>
              {externalCards.map((card, index) => (
                <Col xs={24} sm={12} lg={6} key={`external-${index}`}>
                  <Card
                    styles={{ body: { padding: 0 } }}
                    className={`${styles.dataCard} ${styles.externalDataCard} ${card.clickable ? styles.clickable : ''}`}
                    onClick={card.clickable ? () => handleSectionCardClick('external', card.type) : undefined}
                    data-color="glow"
                  >
                    <div className={styles.cardContent}>
                      <div className={styles.statusTag} style={{ background: card.color, borderColor: '#fff', color: '#fff' }}>
                        {card.title}
                      </div>
                      <div className={styles.statusNumber} style={{ color: card.color }}>
                        {loading ? (
                          <Skeleton.Input active size="small" className={styles.cardSkeleton} />
                        ) : (
                          card.value
                        )}
                      </div>
                      <div className={styles.statusText}>
                        {card.description}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {isAdmin && (
            <div className={styles.sectionBlock}>
              <h3 className={styles.sectionTitle}>Directory</h3>
              <Row gutter={[16, 16]}>
                {directoryCards.map((card, index) => (
                  <Col xs={24} sm={12} lg={6} key={`directory-${index}`}>
                    <Card
                      styles={{ body: { padding: 0 } }}
                      className={styles.dataCard}
                      data-color="glow"
                    >
                      <div className={styles.cardContent}>
                        <div className={styles.statusTag} style={{ background: card.color, borderColor: '#fff', color: '#fff' }}>
                          {card.title}
                        </div>
                        <div className={styles.statusNumber} style={{ color: card.color }}>
                          {loading ? (
                            <Skeleton.Input active size="small" className={styles.cardSkeleton} />
                          ) : (
                            card.value
                          )}
                        </div>
                        <div className={styles.statusText}>
                          {card.description}
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard; 